import { motion, useAnimation, Transition, Variants } from "motion/react"
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { TAnimatedSvgIconProps, TAnimatedSvgIconSize } from "./IconType"

const defaultTransition: Transition = {
  duration: 0.6,
  opacity: { duration: 0.2 },
}

const mainSparkleVariants: Variants = {
  normal: {
    scale: 1,
  },
  animate: {
    scale: [1, 1.1, 1],
  },
}

const sparkVariants: Variants = {
  normal: {
    opacity: 0.5,
  },
  animate: {
    opacity: [0.5, 1, 0.5],
  },
}

const SparklesIcon = forwardRef<TAnimatedSvgIconProps, TAnimatedSvgIconSize>(
  ({ onMouseEnter, onMouseLeave, className, size = 24, ...props }, ref) => {
    const controls = useAnimation()
    const isControlledRef = useRef(false)
    const [isAnimating, setIsAnimating] = useState(false)

    useImperativeHandle(ref, () => {
      isControlledRef.current = true

      return {
        startAnimation: () => {
          setIsAnimating(true)
          void controls.start("animate")
        },
        stopAnimation: () => {
          setIsAnimating(false)
          void controls.start("normal")
        },
      }
    })

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isControlledRef.current) {
          setIsAnimating(true)
          void controls.start("animate")
        } else {
          onMouseEnter?.(e)
        }
      },
      [controls, onMouseEnter],
    )

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (!isControlledRef.current) {
          setIsAnimating(false)
          void controls.start("normal")
        } else {
          onMouseLeave?.(e)
        }
      },
      [controls, onMouseLeave],
    )

    return (
      <div
        className={cn(className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <motion.path
            d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"
            variants={mainSparkleVariants}
            transition={defaultTransition}
            animate={controls}
          />
          <motion.path
            d="M20 3v4"
            variants={sparkVariants}
            transition={{
              duration: 0.5,
              repeat: isAnimating ? Infinity : 0,
            }}
            animate={controls}
          />
          <motion.path
            d="M22 5h-4"
            variants={sparkVariants}
            transition={{
              duration: 0.5,
              repeat: isAnimating ? Infinity : 0,
              delay: 0.25,
            }}
            animate={controls}
          />
          <motion.path
            d="M4 17v2"
            variants={sparkVariants}
            transition={{
              duration: 0.5,
              repeat: isAnimating ? Infinity : 0,
              delay: 0.5,
            }}
            animate={controls}
          />
          <motion.path
            d="M5 18H3"
            variants={sparkVariants}
            transition={{
              duration: 0.5,
              repeat: isAnimating ? Infinity : 0,
              delay: 0.75,
            }}
            animate={controls}
          />
        </svg>
      </div>
    )
  },
)

SparklesIcon.displayName = "SparklesIcon"

export { SparklesIcon }
